// FilterManager: A fully dynamic, configurable filter manager with naming conventions,
// supporting "color-swatch" and "text-swatch" types with disabled state propagation.
class FilterManager {
  constructor(config) {
    // Config options:
    // - optionKey: the filter type (e.g., 'color', 'size')
    // - groupConfig: an array of group objects for this filter type.
    //      Each object should have: color (or group), code, values (array)
    // - groupSelector (optional): CSS selector for the original filter container
    // - groupType: 'color-swatch' (default) or 'text-swatch'
    this.optionKey = config.optionKey || 'color';
    this.groupConfig = config.groupConfig || [];
    this.groupSelector = config.groupSelector || `.filter-group-${this.optionKey}`;
    this.customFilterId = `custom-${this.optionKey}-group-filter`;
    this.initializingMarker = `${this.optionKey}-filter-manager-initializing`;
    this.initialized = false;
    this.valueToGroupMap = {};
    this.groupToCode = {};
    this.groupType = config.groupType || 'color-swatch';

    // Build mapping between individual values and groups,
    // and groups to their corresponding code (or display text for text-swatch)
    this.initMaps();

    // Debounced functions to optimize event handling
    this.debounced = {
      init: this.debounce(this.init.bind(this), 300),
      handleChange: this.debounce(this.handleFilterChange.bind(this), 100)
    };

    this.observer = null;

    // Register this instance globally so that global AJAX events can call its handler.
    if (!window.FilterManagers) {
      window.FilterManagers = [];
    }
    window.FilterManagers.push(this);
  }

  // Build maps: each individual value maps to its group, and each group maps to its code.
  initMaps() {
    this.groupConfig.forEach(group => {
      const groupName = group.group || group.color;
      group.values.forEach(value => {
        this.valueToGroupMap[value] = groupName;
      });
      this.groupToCode[groupName] = group.code;
    });
  }

  // Main initialization routine.
  init() {
    if (document.getElementById(this.initializingMarker) || this.isInitialized()) {
      return;
    }
    const marker = document.createElement('div');
    marker.id = this.initializingMarker;
    marker.style.display = 'none';
    document.body.appendChild(marker);

    try {
      let originalFilter = document.querySelector(this.groupSelector);
      if (!originalFilter) {
        // Fallback: search by heading text (case-insensitive)
        const headings = document.querySelectorAll('.filter-group__heading__text');
        for (const heading of headings) {
          if (heading.textContent.trim().toLowerCase() === this.optionKey.toLowerCase()) {
            originalFilter = heading.closest('.filter-group');
            break;
          }
        }
      }
      if (!originalFilter) {
        console.log(`Original filter for "${this.optionKey}" not found`);
        document.body.removeChild(marker);
        return;
      }

      // Create a mapping from groups to their associated values.
      const groupToValuesMap = {};
      Object.entries(this.valueToGroupMap).forEach(([value, group]) => {
        if (!groupToValuesMap[group]) {
          groupToValuesMap[group] = [];
        }
        groupToValuesMap[group].push(value);
      });

      // Create and insert the custom filter UI before the original filter container.
      this.createCustomFilter(groupToValuesMap, originalFilter);
      // Handle URL parameters for pre-selected values.
      this.handleUrlParameters();
      // Set up event listeners on filters and interactions.
      this.setupFilterHandlers();

      this.initialized = true;
    } catch (e) {
      console.error('Error initializing FilterManager:', e);
    } finally {
      document.body.removeChild(marker);
    }
  }

  // Returns true if our custom filter UI already exists.
  isInitialized() {
    return document.getElementById(this.customFilterId) !== null;
  }

  // Create and insert the custom filter UI.
  // originalFilter: the container where the original (server-rendered) inputs exist.
  createCustomFilter(groupToValuesMap, originalFilter) {
    const newFilterGroup = document.createElement('div');
    newFilterGroup.className = 'filter-group filter-group--swatch filter-group--layout-no-label';
    newFilterGroup.id = this.customFilterId;

    newFilterGroup.innerHTML = `
      <toggle-target role="button" tabindex="0" class="filter-group__heading" data-toggle-target=".filter-group--custom-${this.optionKey}">
        <div class="filter-group__heading__text">${this.optionKey.charAt(0).toUpperCase() + this.optionKey.slice(1)}</div>
        <span class="filter-group__heading__indicator">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
            <path d="m6 9 6 6 6-6"></path>
          </svg>
        </span>
      </toggle-target>
      <div class="filter-group__items filter-group--custom-${this.optionKey} toggle-target">
        <div class="toggle-target-container" id="custom-${this.optionKey}-groups"></div>
      </div>
    `;

    // Insert the new UI just before the original filter.
    originalFilter.parentNode.insertBefore(newFilterGroup, originalFilter);
    const container = newFilterGroup.querySelector(`#custom-${this.optionKey}-groups`);

    // Use the original filter container for disabled state queries.
    const originalContainer = originalFilter;

    Object.entries(groupToValuesMap).forEach(([groupName, values]) => {
      const groupItem = document.createElement('label');
      groupItem.className = 'filter-group__item';
      groupItem.dataset.tag = groupName.toLowerCase();
      groupItem.dataset.swatch = groupName.toLowerCase();

      let extraContent = '';
      if (this.groupType === 'color-swatch' && this.groupToCode[groupName]) {
        groupItem.style.setProperty('--swatch-background-color', this.groupToCode[groupName]);
      } else if (this.groupType === 'text-swatch') {
        extraContent = `<span class="filter-group__swatch-text">${this.groupToCode[groupName]}</span>`;
      }

      groupItem.innerHTML = `
        <input class="filter-group__checkbox" type="checkbox" name="filter.v.option.${this.optionKey}" value="${groupName}">
        ${extraContent}
        <span class="filter-group__item__text">${groupName}</span>
      `;
      container.appendChild(groupItem);

      const groupCheckbox = groupItem.querySelector('input');
      
      // Check the disabled state by scanning all original inputs for this optionKey.
      // For each value in this group, we iterate over all original inputs and compare normalized values.
      let allDisabled = true;
      const originalInputs = originalContainer.querySelectorAll(`input[name="filter.v.option.${this.optionKey}"]`);
      values.forEach(value => {
        originalInputs.forEach(input => {
          if (input.value.trim().toLowerCase() === value.trim().toLowerCase() && !input.disabled) {
            allDisabled = false;
          }
        });
      });
      if (allDisabled) {
        groupCheckbox.disabled = true;
        groupItem.classList.add('disabled'); // Optionally add a CSS class for styling disabled groups.
      }
      
      groupCheckbox.addEventListener('change', () => {
        if (groupCheckbox.checked) {
          this.selectValuesInGroup(values);
          groupItem.classList.add('filter-group__item--active');
        } else {
          this.deselectValuesInGroup(values);
          groupItem.classList.remove('filter-group__item--active');
        }
      });
    });
  }

  // Handle URL parameters so that pre-selected values are reflected.
  handleUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const selectedValues = urlParams.getAll(`filter.v.option.${this.optionKey}`);
    if (selectedValues.length > 0) {
      const groupsToCheck = new Set();
      selectedValues.forEach(value => {
        const group = this.valueToGroupMap[value];
        if (group) groupsToCheck.add(group);
      });
      if (groupsToCheck.size > 0) {
        const groupCheckboxes = document.querySelectorAll(`input[name="filter.v.option.${this.optionKey}"]`);
        groupCheckboxes.forEach(checkbox => {
          if (groupsToCheck.has(checkbox.value)) {
            checkbox.checked = true;
            checkbox.closest('.filter-group__item').classList.add('filter-group__item--active');
          }
        });
      }
    }
  }

  // Select all individual checkboxes for the given group of values.
  selectValuesInGroup(values) {
    const valueCheckboxes = document.querySelectorAll(`input[name="filter.v.option.${this.optionKey}"]`);
    valueCheckboxes.forEach(checkbox => {
      if (values.includes(checkbox.value) && !checkbox.disabled && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // Deselect all individual checkboxes for the given group of values.
  deselectValuesInGroup(values) {
    const valueCheckboxes = document.querySelectorAll(`input[name="filter.v.option.${this.optionKey}"]`);
    valueCheckboxes.forEach(checkbox => {
      if (values.includes(checkbox.value) && checkbox.checked && !checkbox.disabled) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // Set up event handlers on filter-related elements.
  setupFilterHandlers() {
    const allCheckboxes = document.querySelectorAll('.filter-group input[type="checkbox"]');
    allCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.debounced.handleChange();
      });
    });
    const toggles = document.querySelectorAll('[data-toggle-target]');
    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        this.debounced.handleChange();
      });
    });
  }

  // Called on any filter change; reinitialize the custom filter UI if missing.
  handleFilterChange() {
    setTimeout(() => {
      if (!document.getElementById(this.customFilterId)) {
        this.init();
      }
    }, 500);
  }

  // Set up a MutationObserver to watch for DOM changes in filters.
  setupObserver() {
    if (this.observer) return;
    this.observer = new MutationObserver(mutations => {
      const filterChanged = mutations.some(mutation => {
        const isFilter = node =>
          node && (node.classList?.contains('filter-group') ||
                   (node.querySelector && node.querySelector('.filter-group')));
        if (isFilter(mutation.target)) return true;
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && isFilter(node)) return true;
        }
        for (const node of mutation.removedNodes) {
          if (node.nodeType === 1 && isFilter(node)) return true;
        }
        return false;
      });
      if (filterChanged && !document.getElementById(this.customFilterId)) {
        this.debounced.init();
      }
    });
    const mainContainer = document.querySelector('main') || document.body;
    if (mainContainer) {
      this.observer.observe(mainContainer, { childList: true, subtree: true });
    }
  }

  // Set up a global click handler for filter-related interactions.
  setupGlobalClickHandler() {
    document.addEventListener('click', event => {
      const isRelated = event.target.closest('.filter-group') ||
                        event.target.closest('[data-toggle-target]') ||
                        event.target.closest('.pagination') ||
                        event.target.closest('select[name="sort_by"]') ||
                        event.target.closest('button[type="submit"]');
      if (isRelated) {
        this.debounced.handleChange();
      }
    });
  }

  // Debounce function utility.
  debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Set up AJAX event listeners so that when an AJAX event occurs,
  // all FilterManager instances handle changes.
  setupAjaxEventListeners() {
    const ajaxEvents = ['ajaxComplete', 'ajaxSuccess', 'ajaxStop'];
    ajaxEvents.forEach(eventName => {
      document.addEventListener(eventName, () => {
        if (window.FilterManagers && Array.isArray(window.FilterManagers)) {
          window.FilterManagers.forEach(manager => manager.debounced.handleChange());
        }
      });
    });

    if (window.fetch) {
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        return originalFetch.apply(this, args).then(response => {
          setTimeout(() => {
            if (window.FilterManagers && Array.isArray(window.FilterManagers)) {
              window.FilterManagers.forEach(manager => manager.debounced.handleChange());
            }
          }, 300);
          return response;
        });
      };
    }

    if (window.XMLHttpRequest) {
      const originalOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (...args) {
        this.addEventListener('load', () => {
          setTimeout(() => {
            if (window.FilterManagers && Array.isArray(window.FilterManagers)) {
              window.FilterManagers.forEach(manager => manager.debounced.handleChange());
            }
          }, 300);
        });
        return originalOpen.apply(this, args);
      };
    }
  }

  // Watch for changes in applied filters and sync custom UI
syncWithAppliedFilters() {
  const observer = new MutationObserver(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const selectedValues = urlParams.getAll(`filter.v.option.${this.optionKey}`);
    const selectedGroups = new Set();

    selectedValues.forEach(value => {
      const group = this.valueToGroupMap[value];
      if (group) selectedGroups.add(group);
    });

    const groupCheckboxes = document.querySelectorAll(`#${this.customFilterId} input[type="checkbox"]`);
    groupCheckboxes.forEach(checkbox => {
      const isChecked = selectedGroups.has(checkbox.value);
      checkbox.checked = isChecked;
      checkbox.closest('.filter-group__item').classList.toggle('filter-group__item--active', isChecked);
    });
  });

  const target = document.querySelector('.active-facets') || document.body;
  observer.observe(target, { childList: true, subtree: true });
}


  // Start the FilterManager: initialize and set up observers/handlers.
  start() {
    this.init();
    this.setupObserver();
    this.setupGlobalClickHandler();
    this.setupAjaxEventListeners();
    this.syncWithAppliedFilters();
  }
}
